import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ResearchResults } from '../components/ResearchResults';
import ReportView from '../components/ReportView';
import { FloatingStatus } from '../components/FloatingStatus';
import { ThemeToggle } from '../components/ThemeToggle';
import { ShareButton } from '../components/ShareButton';
import { HeaderMediaControl } from '../components/HeaderMediaControl';
import { getAuthHeaders, getOdotId } from '../utils/odotId';
import { primeYouTubeCache } from '../utils/youtubeCache';
import type { Report } from '@tastymusic/research-agent';
import './ResultsPage.css';

interface GenreContext {
  description: string;
  keyArtists: { name: string; track: string }[];
  youtubeQueries: string[];
}

interface BandcampEmbedData {
  embedHtml: string;
  albumId?: string;
  title?: string;
}

interface ArtistResult {
  name: string;
  genres: string[];
  era: string | null;
  location: string | null;
  matchReason: string;
  confidence: number;
  tracks: string[];
  relatedArtists: string[];
  links?: {
    bandcamp?: string;
    spotify?: string;
    youtube?: string;
  };
  youtubeVideoId?: string;
  bandcampEmbed?: BandcampEmbedData;
}

interface CachedReportData {
  report: Report;
  youtubeCache: Record<string, string>;
  genreContext?: GenreContext;
  discoveredArtists?: ArtistResult[];
  status?: 'complete' | 'incomplete';
}

interface QueryResult {
  genres: string[];
  mood: string[];
  artistType: string | null;
  likeArtists: string[];
  unlikeArtists: string[];
  modifiers: string[];
  attitude: string[];
  era: string | null;
  location: string | null;
  freeform: string;
}

interface LocationResolved {
  input: string;
  normalized: string | null;
  geonameId?: number;
  population?: number;
  warning?: string;
}

interface LocationState {
  query?: QueryResult;
  locationResolved?: LocationResolved;
}

export function ResultsPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [cachedReport, setCachedReport] = useState<CachedReportData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHealing, setIsHealing] = useState(false);
  const [healingStatus, setHealingStatus] = useState('');
  const [healingBuildingTab, setHealingBuildingTab] = useState<string | undefined>(undefined);

  // For live research mode - get from navigation state
  const query = state?.query || null;
  const locationResolved = state?.locationResolved || null;

  // Redirect to search if no valid state
  useEffect(() => {
    if (!query && !reportId) {
      navigate('/');
    }
  }, [query, reportId, navigate]);

  // Theme state
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('tastymusic-theme');
      if (saved === 'dark' || saved === 'light') return saved;
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('tastymusic-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Load cached report if we have a reportId
  useEffect(() => {
    if (reportId) {
      loadReport(reportId);
    }
  }, [reportId]);

  const loadReport = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/v1/research/report/${id}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.youtubeCache) {
          primeYouTubeCache(data.youtubeCache);
        }
        setCachedReport(data);
        // Auto-heal incomplete or still-generating reports
        if (data.status === 'incomplete' || data.status === 'generating') {
          healReport(id);
        }
      } else {
        console.error('Report not found, checking history for rebuild...');
        // Report not found - check history for originalQuery to rebuild
        await tryRebuildFromHistory(id);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const tryRebuildFromHistory = async (reportId: string) => {
    try {
      const historyResponse = await fetch('/api/v1/research/history', {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        const entry = historyData.searches?.find((s: { reportId: string }) => s.reportId === reportId);
        if (entry) {
          // Use originalQuery if available, otherwise fall back to queryDisplay as freeform
          const originalQuery = entry.originalQuery || {
            freeform: entry.queryDisplay || '',
            genres: [],
            era: null,
            location: null,
          };
          console.log('Found history entry, rebuilding...', originalQuery);
          // Navigate to results page with query to trigger fresh research
          navigate('/results', {
            state: {
              query: {
                genres: originalQuery.genres || [],
                mood: [],
                artistType: null,
                likeArtists: [],
                unlikeArtists: [],
                modifiers: [],
                attitude: [],
                era: originalQuery.era,
                location: originalQuery.location,
                freeform: originalQuery.freeform,
              },
            },
            replace: true, // Replace current URL in history
          });
          return;
        }
      }
    } catch (err) {
      console.error('Failed to check history:', err);
    }
    // No history entry - go home
    navigate('/');
  };

  const healReport = async (id: string) => {
    setIsHealing(true);
    setHealingStatus('Completing report...');

    try {
      const odotId = getOdotId();
      const response = await fetch(`/api/v1/research/report/${id}?heal=true&odotId=${odotId}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Healing request failed');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              handleHealingEvent(data);
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Healing error:', err);
    } finally {
      setIsHealing(false);
      setHealingStatus('');
    }
  };

  const handleHealingEvent = (event: { type: string; [key: string]: unknown }) => {
    switch (event.type) {
      case 'healing':
        setHealingStatus((event as { message?: string }).message || 'Completing report...');
        break;
      case 'context':
        // Handle regenerated or streamed genre context
        const contextData = event as { context?: GenreContext };
        if (contextData.context) {
          setCachedReport(prev => prev ? {
            ...prev,
            genreContext: contextData.context,
          } : null);
        }
        break;
      case 'artist':
        // Handle regenerated or streamed discovered artists
        const artistEvent = event as {
          artist?: ArtistResult;
          youtubeVideoId?: string;
          bandcampEmbed?: BandcampEmbedData;
        };
        if (artistEvent.artist) {
          const newArtist = { ...artistEvent.artist };
          // Include pre-resolved embed data from event
          if (artistEvent.youtubeVideoId) {
            newArtist.youtubeVideoId = artistEvent.youtubeVideoId;
          }
          if (artistEvent.bandcampEmbed) {
            newArtist.bandcampEmbed = artistEvent.bandcampEmbed;
          }
          setCachedReport(prev => {
            if (!prev) return null;
            const existingArtists = prev.discoveredArtists || [];
            // Deduplicate by artist name (case-insensitive)
            if (existingArtists.some(a => a.name.toLowerCase() === newArtist.name.toLowerCase())) {
              return prev;
            }
            return {
              ...prev,
              discoveredArtists: [...existingArtists, newArtist],
            };
          });
        }
        break;
      case 'section':
        const section = (event as { section?: { type: string; data: unknown } }).section;
        if (section) {
          updateReportSection(section.type, section.data);
          setHealingStatus(`Generating ${section.type}...`);
          // Map section type to tab for building indicator
          const sectionToTab: Record<string, string> = {
            'introduction': 'genre',
            'founders': 'genre',
            'scene': 'scenes',
            'mutation': 'mutations',
            'moreArtists': 'artists',
            'infrastructure': 'infrastructure',
            'coda': 'coda',
            'quickReference': 'reference',
          };
          setHealingBuildingTab(sectionToTab[section.type]);
        }
        break;
      case 'report':
        const reportData = event as { report?: Report; youtubeCache?: Record<string, string> };
        if (reportData.report) {
          setCachedReport(prev => prev ? {
            ...prev,
            report: reportData.report!,
            youtubeCache: reportData.youtubeCache || prev.youtubeCache,
            status: 'complete',
          } : null);
        }
        break;
      case 'complete':
        setHealingStatus('');
        setHealingBuildingTab(undefined);
        break;
      case 'error':
        console.error('Healing error:', (event as { message?: string }).message);
        break;
    }
  };

  const updateReportSection = (sectionType: string, data: unknown) => {
    setCachedReport(prev => {
      if (!prev) return null;
      const updatedReport = { ...prev.report };

      switch (sectionType) {
        case 'header':
          Object.assign(updatedReport, data);
          break;
        case 'introduction':
          updatedReport.introduction = data as Report['introduction'];
          break;
        case 'founders':
          updatedReport.founders = data as Report['founders'];
          break;
        case 'scene':
          updatedReport.scenes = [...(updatedReport.scenes || []), data as Report['scenes'][0]];
          break;
        case 'mutation':
          updatedReport.mutations = [...(updatedReport.mutations || []), data as Report['mutations'][0]];
          break;
        case 'moreArtists':
          updatedReport.moreArtists = data as Report['moreArtists'];
          break;
        case 'infrastructure':
          updatedReport.infrastructure = data as Report['infrastructure'];
          break;
        case 'coda':
          updatedReport.coda = data as Report['coda'];
          break;
        case 'quickReference':
          updatedReport.quickReference = data as Report['quickReference'];
          break;
      }

      return { ...prev, report: updatedReport };
    });
  };

  const handleReset = () => {
    navigate('/');
  };

  const getPlatformLinks = (artistName: string) => {
    const encoded = encodeURIComponent(artistName);
    return {
      bandcamp: `https://bandcamp.com/search?q=${encoded}`,
      spotify: `https://open.spotify.com/search/${encoded}`,
      apple: `https://music.apple.com/search?term=${encoded}`,
      tidal: `https://tidal.com/search?q=${encoded}`,
      amazon: `https://music.amazon.com/search/${encoded}`,
    };
  };

  // Loading state
  if (isLoading && !cachedReport) {
    return (
      <div className="results-page" data-theme={theme}>
        <header className="results-header">
          <img
            src="/logo.png"
            alt=""
            className="logo-image"
            onClick={handleReset}
          />
          <div className="header-title">Loading...</div>
        </header>
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  // Live research mode (streaming results)
  if (query && !cachedReport) {
    const locationDisplay = locationResolved?.normalized && locationResolved.input !== locationResolved.normalized
      ? `${query.freeform.replace(locationResolved.input, locationResolved.normalized)}`
      : query.freeform;

    return (
      <div className="results-page" data-theme={theme}>
        <header className="results-header">
          <img
            src="/logo.png"
            alt=""
            className="logo-image"
            onClick={handleReset}
          />
          <div className="header-title">
            <span className="query-text">{locationDisplay}</span>
            {locationResolved?.normalized && locationResolved.population && (
              <span className="location-info">
                {locationResolved.normalized} (pop. {locationResolved.population.toLocaleString()})
              </span>
            )}
          </div>
          <HeaderMediaControl />
          <ShareButton />
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        </header>

        <main className="results-content">
          <ResearchResults query={query} onReset={handleReset} theme={theme} />
        </main>
      </div>
    );
  }

  // Cached report view
  if (cachedReport) {
    const context = cachedReport.genreContext;
    const artists = cachedReport.discoveredArtists || [];
    const headline = cachedReport.report.title || 'Music Discovery';

    return (
      <div className="results-page" data-theme={theme}>
        <header className="results-header">
          <img
            src="/logo.png"
            alt=""
            className="logo-image"
            onClick={handleReset}
          />
          <div className="header-title">
            <span className="query-text">{headline}</span>
          </div>
          <HeaderMediaControl />
          <span className="cached-badge">cached</span>
          <ShareButton reportId={reportId} />
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        </header>

        <main className="results-content">
          <div className="report-container">
            <ReportView
              report={cachedReport.report}
              youtubeCache={cachedReport.youtubeCache}
              theme={theme}
              genreContext={context || undefined}
              discoveredArtists={artists}
              extraArtists={artists.slice(6)}
              getPlatformLinks={getPlatformLinks}
              buildingTab={isHealing ? healingBuildingTab as any : undefined}
            />
          </div>

          <div className="results-actions">
            <button className="reset-button" onClick={handleReset}>
              New search
            </button>
          </div>
        </main>

        <FloatingStatus
          phase="research"
          message={healingStatus}
          isActive={isHealing}
        />
      </div>
    );
  }

  // Fallback - show loading or redirect will happen via useEffect
  return (
    <div className="results-page" data-theme={theme}>
      <header className="results-header">
        <img
          src="/logo.png"
          alt=""
          className="logo-image"
          onClick={handleReset}
        />
        <div className="header-title">Loading...</div>
      </header>
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    </div>
  );
}
